/**
 * The palette: every block the catalogue holds, grouped by what it is for.
 *
 * §7 of develop/DungeonBlocks.md is where the possibilities come from, and §8.1 says
 * there are three actions and placing is the first of them -- so the palette is a list
 * of *places with a purpose* rather than a toolbox, and picking one is the whole of its
 * job. It never says whether a block may be placed: that is the three rules' answer,
 * and the Build screen asks them once the cursor is over a cell.
 *
 * The catalogue is handed in rather than fetched here. `world/blockCatalogue.ts` is the
 * one place `/assets/modules/blocks.json` is named -- the client's only asset index
 * rule, applied to blocks as the pack manifest applies it to art -- and a level, the
 * view and this list all read the same document through it.
 */
import { BLOCK_ROLES, type BlockCatalogue, type BlockId, type BlockRole } from '@dungeon-builder/shared';

/** What the palette calls each role. The roles themselves are shared; these are copy. */
const ROLE_LABEL: Record<BlockRole, string> = {
  link: 'Links',
  room: 'Rooms',
  goal: 'Objectives',
  threat: 'Threats',
};

export interface BlockPaletteOptions {
  catalogue: BlockCatalogue;
  /** Fires on every pick, including re-picking the block already in hand. */
  onPick(id: BlockId): void;
}

export class BlockPalette {
  private readonly root: HTMLElement;
  private readonly buttons = new Map<BlockId, HTMLButtonElement>();
  private picked: BlockId | null = null;

  constructor(container: HTMLElement, options: BlockPaletteOptions) {
    this.root = document.createElement('div');
    this.root.className = 'build-palette';
    container.append(this.root);

    const ids = Object.keys(options.catalogue);
    if (ids.length === 0) {
      // The same honest failure `loadBlockCatalogue` makes: no catalogue is a page
      // that says so, rather than an empty list that looks like twelve missing blocks.
      const empty = document.createElement('p');
      empty.className = 'build-empty';
      empty.textContent = 'No block catalogue. Author one in the admin editor’s Modules workspace.';
      this.root.append(empty);
      return;
    }

    // Grouped in the roles' own order, and within a role in the catalogue's -- which is
    // §5.5's order, so the entrance leads the objectives rather than the vault.
    for (const role of BLOCK_ROLES) {
      const inRole = ids.filter((id) => options.catalogue[id]?.role === role);
      if (inRole.length === 0) continue;

      const heading = document.createElement('h3');
      heading.textContent = ROLE_LABEL[role];
      this.root.append(heading);

      for (const id of inRole) {
        const definition = options.catalogue[id];
        if (!definition) continue;
        const { width, depth } = definition.footprint;
        const ports = definition.ports.length;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'build-block';
        button.dataset['take'] = id;
        button.setAttribute('aria-pressed', 'false');
        button.innerHTML =
          `<span class="build-block-label"></span>` +
          `<span class="build-block-shape">${width}×${depth} · ${ports} port${ports === 1 ? '' : 's'}</span>`;
        // The label is the author's text, so it goes in as text rather than as markup.
        const label = button.querySelector('.build-block-label');
        if (label) label.textContent = definition.label;
        button.title = id;
        button.addEventListener('click', () => options.onPick(id));

        this.buttons.set(id, button);
        this.root.append(button);
      }
    }
  }

  get selected(): BlockId | null {
    return this.picked;
  }

  /** Shows which block is in hand. The Build screen owns that fact; this reflects it. */
  select(id: BlockId | null): void {
    this.picked = id;
    for (const [blockId, button] of this.buttons) {
      button.setAttribute('aria-pressed', String(blockId === id));
    }
  }

  dispose(): void {
    this.root.remove();
    this.buttons.clear();
  }
}
